import type { DraftJson } from '../routes/CardCustomizer/DraftJson.types';
import type { PageDimensions } from '../routes/CardCustomizer/components/PageJson.types';
import { isPostcardCardSize } from './cardSizeName';
import { getDimensions } from './index';

/** Layout id of a back the user has not chosen yet. Looks like `blank`, means "undecided". */
export const PLACEHOLDER_THEME_ID = 'placeholder';

type PlaceholderBack = NonNullable<DraftJson['card']['back']>;

/** Unlike a chosen `blank`, a placeholder back never gets handwriting guide lines. */
export const isPlaceholderBack = (back: Readonly<{ layout?: string }> | undefined): boolean => back?.layout === PLACEHOLDER_THEME_ID;

/** Postcard backs are landscape (6x4) under a portrait (4x6) front; every other back matches its front. */
export const placeholderBackDimensions = (front: PageDimensions): PageDimensions => {
  const { width, height, cutMargin, safeZoneMargin } = front;
  const rotate = isPostcardCardSize(width, height) && width < height;
  return { width: rotate ? height : width, height: rotate ? width : height, cutMargin, safeZoneMargin };
};

/**
 * The back a new draft starts on; `CardBackChoiceGroup` swaps in the default on the first visit
 * to Edit Back. Sized here because a draft can reach Review without that visit, and a size-less
 * postcard back renders portrait. A factory: Apollo deep-freezes mutation variables in dev.
 */
export const createPlaceholderBack = (front: PageDimensions | undefined): PlaceholderBack => ({
  layout: PLACEHOLDER_THEME_ID,
  fill: 'rgb(255,255,255)',
  layers: [],
  ...(front ? { dimensions: placeholderBackDimensions(front) } : {}),
});

/** Sizes a placeholder persisted before placeholders carried a size. */
export const ensurePlaceholderBackDimensions = (card: DraftJson['card']): void => {
  const back = card.back;
  if (!back || back.layout !== PLACEHOLDER_THEME_ID || back.dimensions) return;
  back.dimensions = placeholderBackDimensions(getDimensions(card));
};
